import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  prenom: "", nom: "", email: "", telephone: "",
  type_demande: "contact", sujet: "", nombre_personnes: "", date_souhaitee: "", message: "",
};

const Contact = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.prenom || !form.nom || !form.email || !form.message) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        nombre_personnes: form.nombre_personnes ? parseInt(form.nombre_personnes, 10) : null,
      };
      await axios.post(`${API}/contact`, payload);
      toast.success("Votre demande a bien été envoyée. Nous vous répondrons rapidement !");
      setForm(empty);
    } catch (err) {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const isVisite = form.type_demande === "visite";

  return (
    <section data-testid="contact-section" id="contact" className="py-24 lg:py-32 bg-creme">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-sm uppercase tracking-[0.3em] text-mousse font-500">À votre écoute</span>
          <h2 className="font-heading text-4xl sm:text-5xl text-foreground mt-2 tracking-tight">Contact</h2>
          <p className="text-lg text-foreground/75 mt-5 leading-relaxed">
            Une envie, une commande, le souhait de visiter l'exploitation ou de partager une dégustation ?
            Confiez-nous votre message : ce sera un plaisir de vous répondre et de vous accueillir.
          </p>

          <div className="space-y-5 mt-9">
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-full bg-bordeaux/10 text-bordeaux flex items-center justify-center"><Phone size={19} /></span>
              <div><div className="text-sm text-foreground/55">Téléphone</div><div className="font-600 text-foreground">06 62 12 51 92</div></div>
            </div>
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-full bg-bordeaux/10 text-bordeaux flex items-center justify-center"><Mail size={19} /></span>
              <div><div className="text-sm text-foreground/55">Email</div><div className="font-600 text-foreground">escargholala@gmail.com</div></div>
            </div>
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-full bg-bordeaux/10 text-bordeaux flex items-center justify-center"><MapPin size={19} /></span>
              <div><div className="text-sm text-foreground/55">Adresse</div><div className="font-600 text-foreground">982 Rue Principale, 71460 Savianges, Bourgogne</div></div>
            </div>
          </div>

          <div data-testid="map-savianges" className="mt-8 rounded-2xl overflow-hidden border border-border shadow-[0_10px_30px_rgba(44,42,40,0.1)]">
            <iframe
              title="Carte - Escarg'Holala à Savianges"
              src="https://www.google.com/maps?q=982%20Rue%20Principale%2C%2071460%20Savianges&output=embed"
              width="100%"
              height="300"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-sable rounded-3xl p-8 lg:p-10 shadow-[0_18px_45px_rgba(44,42,40,0.1)] border border-border space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label htmlFor="prenom">Prénom *</Label>
              <Input data-testid="input-prenom" id="prenom" value={form.prenom} onChange={(e) => set("prenom", e.target.value)} placeholder="Marie" className="bg-white" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nom">Nom *</Label>
              <Input data-testid="input-nom" id="nom" value={form.nom} onChange={(e) => set("nom", e.target.value)} placeholder="Dupont" className="bg-white" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email *</Label>
              <Input data-testid="input-email" id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="marie@email.fr" className="bg-white" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="telephone">Téléphone</Label>
              <Input data-testid="input-telephone" id="telephone" value={form.telephone} onChange={(e) => set("telephone", e.target.value)} placeholder="06 12 34 56 78" className="bg-white" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Type de demande</Label>
            <Select value={form.type_demande} onValueChange={(v) => set("type_demande", v)}>
              <SelectTrigger data-testid="select-type" className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="contact" data-testid="type-contact">Question / Renseignement</SelectItem>
                <SelectItem value="commande" data-testid="type-commande">Commande de produits</SelectItem>
                <SelectItem value="visite" data-testid="type-visite">Réservation de visite & dégustation</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isVisite && (
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label htmlFor="nb">Nombre de personnes</Label>
                <Input data-testid="input-nb-personnes" id="nb" type="number" min="1" value={form.nombre_personnes} onChange={(e) => set("nombre_personnes", e.target.value)} placeholder="4" className="bg-white" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="date">Date souhaitée</Label>
                <Input data-testid="input-date" id="date" type="date" value={form.date_souhaitee} onChange={(e) => set("date_souhaitee", e.target.value)} className="bg-white" />
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="sujet">Sujet</Label>
            <Input data-testid="input-sujet" id="sujet" value={form.sujet} onChange={(e) => set("sujet", e.target.value)} placeholder="Objet de votre message" className="bg-white" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="message">Message *</Label>
            <Textarea data-testid="input-message" id="message" rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Votre message..." className="bg-white resize-none" />
          </div>

          <button
            data-testid="submit-contact"
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-bordeaux text-white font-600 px-7 py-3.5 hover:bg-bordeaux-dark transition-all flex items-center justify-center gap-2 disabled:opacity-70"
          >
            {loading ? <><Loader2 size={18} className="animate-spin" /> Envoi...</> : <><Send size={18} /> Envoyer ma demande</>}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
